import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import TableContainer from '../../../../components/TableContainerReactTable';
import { Placement } from '../../../../types/placement';
import numeral from 'numeral';
import { confirmImportPlacements, importPlacements } from '../../../../api/placementApi';
import { useScopes } from '../../../../hooks/useScopes';
import { ApiScope } from '../../../../types/api-scopes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight, X } from 'lucide-react';

interface ImportPlacementModalProps {
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Placement[]) => void;
}

const ImportPlacementModal = ({ isOpen, toggle, onAddData }: ImportPlacementModalProps) => {
    const { hasScope } = useScopes();
    const [activeTab, setActiveTab] = useState<'import' | 'confirm' | 'success'>('import');
    const [firstStepLoading, setFirstStepLoading] = useState<boolean>(false);
    const [secondStepLoading, setSecondStepLoading] = useState<boolean>(false);
    const [placementsToSave, setPlacementsToSave] = useState<(Placement & { errors: string[]; warnings: string[] })[]>([]);
    numeral.defaultFormat('0b');

    const toggleImportPlacementModal = () => {
        toggle();
        setActiveTab('import');
        handleFirstStepForm.resetForm();
        setPlacementsToSave([]);
    };

    const placementsToAddColumns = [
        {
            header: 'ID do Placement',
            accessorKey: 'adSpacePlacementId',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Nome do placement',
            accessorKey: 'name',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tipo',
            accessorKey: 'tipo',
            cell: (cell: any) => {
                const tipo = cell.getValue();
                let badgeClass = 'bg-primary text-primary';
                if (tipo === 'display') badgeClass = 'bg-green-600 text-white';
                else if (tipo === 'video') badgeClass = 'bg-yellow-600 text-white';
                else if (tipo === 'audio') badgeClass = 'bg-blue-600 text-white';
                return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>{tipo}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Sizes',
            accessorKey: 'sizes',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell: any[] = [];
                row.sizes?.forEach((size: string) => {
                    actionCell.push(<span className="px-2 py-1 rounded-full text-xs border border-primary text-primary">{size}</span>);
                });
                return <div className="flex gap-2 flex-wrap">{actionCell}</div>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tamanhos Expandidos',
            accessorKey: 'sizesExpandidos',
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.sizesExpandidos && row.sizesExpandidos.length > 0) {
                    const actionCell: any[] = [];
                    row.sizesExpandidos?.forEach((sizeExpandido: string) => {
                        actionCell.push(<span className="px-2 py-1 rounded-full text-xs border border-secondary text-secondary">{sizeExpandido}</span>);
                    });
                    return <div className="flex gap-2 flex-wrap">{actionCell}</div>;
                }
                return '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Mídia Kit',
            accessorFn: (row: any) => {
                return row.mediaKitUrl || row.mediaKitFile;
            },
            cell: (cell: any) => {
                const url = cell.getValue();
                return url ? <>{url} <a href={url} target="_blank" rel="noopener noreferrer"><i className="ri-external-link-line"></i></a></> : '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Rich Media',
            accessorKey: 'maxRichMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxRichMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Imagem',
            accessorKey: 'maxImageSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxImageSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Aceita Audio/Video',
            accessorKey: 'acceptsAudioVideo',
            cell: (cell: any) => {
                const row = cell.row.original;
                const className = row.acceptsAudioVideo ? 'bg-green-600 text-white' : 'bg-red-600 text-white';
                return <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>{row.acceptsAudioVideo ? 'sim' : 'não'}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Audio/Video',
            accessorKey: 'maxMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tempo máximo de vídeo/audio (s)',
            assessorKey: 'maxMediaDurationInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.maxMediaDurationInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Recarregamento (s)',
            assessorKey: 'refreshInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.refreshInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Obs',
            accessorKey: 'notes',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Erros e Avisos',
            cell: (cell: any) => {
                const row = cell.row.original;
                const errors = row.errors.length === 0 ? '' : row.errors.map((error: string) => (
                    <span className="px-2 py-1 rounded-full text-xs border border-red-600 text-red-600 block mb-1">{error}</span>
                ));
                const warnings = row.warnings.length === 0 ? '' : row.warnings.map((warning: string) => (
                    <span className="px-2 py-1 rounded-full text-xs border border-yellow-600 text-yellow-600 block mb-1">{warning}</span>
                ));
                return (<div className="flex flex-col gap-1">{errors}{warnings}</div>);
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
    ];

    if (hasScope(ApiScope.PLACEMENT_WRITE)) {
        placementsToAddColumns.splice(1, 0, {
            header: 'Publisher',
            accessorFn: (row: any) => row.publisher?.name || '-',
            cell: (cell: any) => cell.getValue(),
            enableSorting: true,
            enableColumnFilter: false,
        });
    }

    const handleFirstStepForm: any = useFormik({
        enableReinitialize: true,

        initialValues: {
            file: undefined,
        },

        validationSchema: Yup.object({
            file: Yup.mixed().required('É necessário um arquivo csv'),
        }),
        onSubmit: () => {
            setFirstStepLoading(true);
            const formData = new FormData();
            const file: HTMLInputElement | null = document.querySelector('#file-field');
            if (file && file.files) {
                formData.append('file', file.files[0]);
            }
            return importPlacements(formData)
                .then((res: any) => {
                    setPlacementsToSave(res);
                    setActiveTab('confirm');
                })
                .catch(() => {
                })
                .finally(() => {
                    setFirstStepLoading(false);
                });
        },
    });

    // Second Step Form Validation
    const handleSecondStepForm = (event: any) => {
        event.preventDefault();
        const placementsToSaveCount = placementsToSave.length;
        if (!window.confirm(`Deseja realmente importar ${placementsToSaveCount} placements?`)) {
            return;
        }

        const countWarnings = placementsToSave.filter((placement) => placement.warnings.length !== 0).length;
        if (countWarnings) {
            if (!window.confirm(`Existem ${countWarnings} placements com alertas. Deseja seguir mesmo assim?`)) {
                return;
            }
        }
        const countErrors = placementsToSave.filter((placement) => placement.errors.length !== 0).length;
        if (countErrors) {
            window.alert(`Existem ${countErrors} placements com erros. Não é possível importar placements com erros.`);
            return;
        }

        setSecondStepLoading(true);

        confirmImportPlacements(placementsToSave)
            .then((res: any) => {
                toggleImportPlacementModal();
                toast.success('Blocos de anúncios importados com sucesso!');
                onAddData(res);
            })
            .catch(() => {
            })
            .finally(() => {
                setSecondStepLoading(false);
            });
    };

    return (
        <Dialog open={isOpen} onOpenChange={toggleImportPlacementModal}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Importar blocos de anúncios</DialogTitle>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="import" onClick={() => activeTab === 'confirm' && setActiveTab('import')}>
                            Importe
                        </TabsTrigger>
                        <TabsTrigger value="confirm" disabled={placementsToSave.length === 0}>
                            Confirme
                        </TabsTrigger>
                        <TabsTrigger value="success" disabled={activeTab !== 'success'}>
                            Concluído
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="import" className="space-y-4 mt-4">
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFirstStepForm.handleSubmit();
                            return false;
                        }} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="file-field">
                                    Blocos de anúncios - CSV
                                </Label>
                                <Input
                                    type="file"
                                    id="file-field"
                                    name="file"
                                    accept=".csv"
                                    onChange={handleFirstStepForm.handleChange}
                                    onBlur={handleFirstStepForm.handleBlur}
                                    value={handleFirstStepForm.values.file}
                                />
                                {handleFirstStepForm.touched.file && handleFirstStepForm.errors.file && (
                                    <p className="text-sm text-red-500">{handleFirstStepForm.errors.file}</p>
                                )}
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    type="submit"
                                    disabled={firstStepLoading}
                                    className="flex items-center gap-2"
                                >
                                    {firstStepLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Carregando...
                                        </>
                                    ) : (
                                        <>
                                            Importar
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </TabsContent>

                    <TabsContent value="confirm" className="space-y-4 mt-4">
                        <TableContainer
                            divClass="table-responsive"
                            columns={placementsToAddColumns}
                            data={placementsToSave}
                            isGlobalFilter={true}
                            customPageSize={30}
                            SearchPlaceholder="Procurar..."
                        />

                        <div className="flex justify-between">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setActiveTab('import')}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Cancelar & Voltar
                            </Button>
                            <Button
                                type="button"
                                disabled={secondStepLoading}
                                onClick={handleSecondStepForm}
                                className="flex items-center gap-2"
                            >
                                {secondStepLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Carregando...
                                    </>
                                ) : (
                                    <>
                                        Confirmar
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </TabsContent>

                    <TabsContent value="success" className="mt-4">
                        <div className="text-center py-8">
                            <div className="flex justify-center mb-4">
                                <div className="rounded-full bg-green-100 p-6">
                                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Muito bom!</h3>
                            <p className="text-muted-foreground">
                                Os blocos de anúncios foram importados com sucesso.
                            </p>
                            <div className="mt-6">
                                <Button onClick={toggleImportPlacementModal} className="flex items-center gap-2">
                                    <X className="h-4 w-4" />
                                    Fechar
                                </Button>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

export default ImportPlacementModal;
